import { ApiError } from '../../api/client';
import type { ImportPreview } from '../../api/endpoints/signup';
import { CategorySchema } from '../../api/schemas';
import { zonesFor, type SignupCountry } from '../../lib/country';
import type { SignupDraft } from './draft';

/** How each prefilled answer is named in "Listo, trajimos ...". */
const FOUND_LABEL: readonly [keyof SignupDraft, string][] = [
  ['businessName', 'el nombre'],
  ['category', 'el tipo de negocio'],
  ['neighborhood', 'la zona'],
  ['address', 'la dirección'],
  ['phone', 'el teléfono'],
  ['pin', 'la ubicación'],
  ['hours', 'el horario'],
];

/**
 * What an import found, as a draft patch. Purely a prefill: every value
 * lands in a question the person still sees and can change. Values the
 * wizard cannot use (an unknown category, a zone from another country) are
 * left out rather than guessed.
 */
export function prefillFromImport(fields: ImportPreview['fields'], country: SignupCountry): Partial<SignupDraft> {
  const category = CategorySchema.safeParse(fields.category);
  const zone = zonesFor(country).find((value) => value === fields.neighborhood);
  const hasPin = fields.lat !== undefined && fields.lng !== undefined;
  return {
    ...(fields.name ? { businessName: fields.name } : {}),
    ...(category.success ? { category: category.data } : {}),
    ...(zone ? { neighborhood: zone } : {}),
    ...(fields.address ? { address: fields.address } : {}),
    ...(fields.phone || fields.whatsapp ? { phone: fields.phone ?? fields.whatsapp } : {}),
    ...(hasPin ? { pin: { lat: fields.lat!, lng: fields.lng! } } : {}),
    ...(fields.hours?.length
      ? { hours: fields.hours.map((hour) => ({ dayOfWeek: hour.dayOfWeek, opensAt: hour.opensAt, closesAt: hour.closesAt, isClosed: false })) }
      : {}),
  };
}

/** What the website had that the signup does not ask for yet, said plainly ("2 servicios", "tu Instagram"). */
export function websiteExtras(fields: ImportPreview['fields']): string[] {
  const count = fields.services?.length ?? 0;
  return [
    ...(count > 0 ? [count === 1 ? '1 servicio' : `${count} servicios`] : []),
    ...(fields.instagram ? ['tu Instagram'] : []),
    ...(fields.facebook ? ['tu Facebook'] : []),
    ...(fields.logoUrl ? ['tu logo'] : []),
  ];
}

const LINK = /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i;

/** A web address (with or without https://) goes to the crawler; anything else is pasted text. */
export function isImportLink(text: string): boolean {
  return LINK.test(text.trim());
}

export function foundLabels(patch: Partial<SignupDraft>): string[] {
  return FOUND_LABEL.filter(([key]) => patch[key] !== undefined).map(([, label]) => label);
}

export function importErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 400) return 'No pudimos abrir ese enlace. Usa la dirección de tu página web, o un enlace de Google, Degusta u OpenTable.';
    if (error.status === 429) return 'Demasiados intentos. Espera un momento.';
    if (error.status === 503) return 'Traer datos no está disponible todavía.';
  }
  return 'No pudimos traer esos datos. Sigue y los escribes tú, son pocas preguntas.';
}
